import { Component, ElementRef, HostListener, ViewChild, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { BatchPickService } from '../batchpick.service';
import { BatchPickState, BasketDetail, MedicationDetail } from '../models/batchpick-state';
import { NsTrayPrintService } from '../../Services/ns-tray-print.service';
import { BatchService } from '../../Services/batch.service';
import { PicklistService } from '../../Services/picklist.service';
import { RichTextLine } from '../../models/ns-tray-rich-text.models';
import { BoxGridComponent } from './box-grid/box-grid.component';
import { ActivityFeedComponent } from './activity-feed/activity-feed.component';
import { CurrentScanPanelComponent, LastScan } from './current-scan-panel/current-scan-panel.component';

@Component({
  selector: 'app-batchpick-pick',
  standalone: true,
  imports: [CommonModule, FormsModule, BoxGridComponent, ActivityFeedComponent, CurrentScanPanelComponent],
  templateUrl: './pick.component.html',
  styleUrl: './pick.component.css',
})
export class PickComponent implements OnInit, OnDestroy {
  @ViewChild('scanInput') scanInput!: ElementRef<HTMLInputElement>;

  batchId!: string;
  state?: BatchPickState;
  barcode = '';
  banner: { type: 'success' | 'warn' | 'error'; text: string } | null = null;
  currentBasket: number | null = null;
  pulseBasket: number | null = null;
  private scanSeq = 0;
  lastScan: LastScan | null = null;
  loading = false;
  finishing = false;

  layout: 'tiles' | 'grid' = 'tiles';
  private readonly layoutKey = 'safescan.batchpick.layout';

  // basket-label start number
  showStartDialog = false;
  startNumber = 1;
  private readonly startNumberKey = 'safescan.batchpick.startNumber';

  openStartDialog(): void {
    try {
      const saved = localStorage.getItem(this.startNumberKey);
      const n = saved ? parseInt(saved, 10) : 1;
      this.startNumber = Number.isFinite(n) && n >= 1 ? n : 1;
    } catch { this.startNumber = 1; }
    this.showStartDialog = true;
  }

  cancelStart(): void { this.showStartDialog = false; }

  confirmStart(): void {
    const n = Math.floor(this.startNumber);
    if (!Number.isFinite(n) || n < 1) {
      this.banner = { type: 'error', text: 'Start basket number must be 1 or greater.' };
      return;
    }
    try { localStorage.setItem(this.startNumberKey, String(n)); } catch { /* private-mode safe */ }
    this.showStartDialog = false;
    this.api.startPick(this.batchId, n).subscribe({
      next: () => this.refresh(),
      error: () => { this.banner = { type: 'error', text: 'Could not start picking — retry.' }; },
    });
  }

  feedDrawerOpen = false;
  toggleFeedDrawer(): void { this.feedDrawerOpen = !this.feedDrawerOpen; }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.showStartDialog) { this.showStartDialog = false; return; }
    if (this.showPrintPanel) { this.showPrintPanel = false; return; }
    if (this.feedDrawerOpen) this.feedDrawerOpen = false;
  }

  setLayout(next: 'tiles' | 'grid'): void {
    this.layout = next;
    if (next === 'tiles') this.feedDrawerOpen = false;
    try { localStorage.setItem(this.layoutKey, next); } catch { /* private-mode safe */ }
  }

  // basket-label print panel
  showPrintPanel = false;
  labelSelection: { number: number; checked: boolean }[] = [];
  private alertsSub?: Subscription;

  get nsPrinters(): string[] { return this.nsTray.availablePrinters; }
  get nsSelectedPrinter(): string { return this.nsTray.selectedPrinter; }
  onPrinterChange(name: string): void { this.nsTray.setPrinter(name); }

  openPrintPanel(): void {
    const numbers = (this.state?.baskets ?? [])
      .map(b => b.basketNumber)
      .sort((a, b) => a - b);
    this.labelSelection = numbers.map(n => ({ number: n, checked: true }));
    this.showPrintPanel = true;
  }

  closePrintPanel(): void { this.showPrintPanel = false; }

  setAllLabels(checked: boolean): void {
    this.labelSelection.forEach(l => (l.checked = checked));
  }

  printSelectedLabels(): void {
    const chosen = this.labelSelection.filter(l => l.checked);
    if (chosen.length === 0) { this.closePrintPanel(); return; }
    if (!this.nsTray.isConnected || !this.nsTray.selectedPrinter) {
      this.banner = { type: 'error', text: 'Cannot print — NS Tray not connected or no printer selected.' };
      return; // keep the panel open so the user can fix the printer and retry
    }
    for (const l of chosen) {
      this.nsTray.print(this.basketLabelLines(l.number));
    }
    this.banner = {
      type: 'success',
      text: `Printing ${chosen.length} basket label${chosen.length === 1 ? '' : 's'}.`,
    };
    this.closePrintPanel();
  }

  private basketLabelLines(n: number): RichTextLine[] {
    return [
      { text: 'Basket', size: 9, align: 'center', offsetTop: 4 },
      { text: String(n), size: 36, bold: true, align: 'center' },
    ];
  }

  ngOnDestroy(): void {
    this.alertsSub?.unsubscribe();
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private api: BatchPickService,
    private nsTray: NsTrayPrintService,
    private batches: BatchService,
    private picklist: PicklistService,
  ) {}

  printPickList(): void {
    const popup = this.picklist.openWindow();
    if (!popup) {
      this.banner = { type: 'error', text: 'The pick list window was blocked. Allow pop-ups for SafeScan and try again.' };
      return;
    }
    this.batches.getBatchById(this.batchId).subscribe({
      next: (res: any) => {
        if (res?.result) this.picklist.write(popup, res.result);
        else popup.close();
      },
      error: () => {
        popup.close();
        this.banner = { type: 'error', text: 'Could not load the pick list — retry.' };
      },
    });
  }

  ngOnInit(): void {
    this.batchId = this.route.snapshot.paramMap.get('batchId')!;
    try {
      const saved = localStorage.getItem(this.layoutKey);
      if (saved === 'tiles' || saved === 'grid') this.layout = saved;
    } catch { /* private-mode safe */ }
    this.refresh();
    this.nsTray.connect();
    this.alertsSub = this.nsTray.alerts$.subscribe(a =>
      this.banner = { type: a.type === 'warning' ? 'warn' : a.type, text: a.message });
  }

  refresh(): void {
    this.loading = true;
    this.api.state(this.batchId).subscribe({
      next: s => {
        this.state = s;
        this.loading = false;
        queueMicrotask(() => this.scanInput?.nativeElement.focus());
      },
      error: () => { this.loading = false; },
    });
  }

  scan(): void {
    const code = this.barcode.trim();
    if (!code) return;
    this.barcode = '';
    this.api.pickScan(this.batchId, code).subscribe({
      next: r => {
        if (r.kind === 'Match') {
          this.banner = { type: 'success', text:
            `Basket ${r.basketNumber} — ${r.medicationName} (${r.basketFill})${r.isSubstitute ? ' · ALT' : ''}` };
          if (r.basketNumber != null) {
            this.currentBasket = r.basketNumber;
            this.triggerPulse(r.basketNumber);
            const seq = ++this.scanSeq;
            this.api.state(this.batchId).subscribe(s => {
              if (seq !== this.scanSeq) return;
              this.state = s;
              const basket = s.baskets.find(b => b.basketNumber === r.basketNumber);
              const med = basket?.medications.find(m => m.medicationId === r.medicationId);
              this.lastScan = (basket && med) ? { basket, med } : null;
              queueMicrotask(() => this.scanInput?.nativeElement.focus());
            });
            return;
          }
          this.refresh();
        } else if (r.kind === 'AlreadyPicked') {
          this.banner = { type: 'warn', text: `Already picked into basket #${r.basketNumber}.` };
        } else {
          this.banner = { type: 'error', text: r.message ?? 'Not in this batch.' };
        }
        queueMicrotask(() => this.scanInput?.nativeElement.focus());
      },
      error: () => {
        this.banner = { type: 'error', text: 'Scan failed — retry.' };
      },
    });
  }

  get medCount(): number {
    if (!this.state) return 0;
    return this.state.baskets.reduce((sum, b) => sum + b.medications.length, 0);
  }

  // Includes `Missing` — for the header, anything terminally resolved
  // (picked, substituted, or marked missing) reduces the "remaining" count.
  // Contrast BoxGridComponent.isPicked which excludes Missing for the per-tile progress bar.
  get resolvedCount(): number {
    if (!this.state) return 0;
    return this.state.baskets.reduce((sum, b) =>
      sum + b.medications.filter(m =>
        m.pickScanStatus === 'Picked' ||
        m.pickScanStatus === 'PickedAsSubstitute' ||
        m.pickScanStatus === 'Missing').length, 0);
  }
  get remainingCount(): number { return this.medCount - this.resolvedCount; }
  get completeBaskets(): number {
    return this.state?.baskets.filter(b => this.basketComplete(b)).length ?? 0;
  }
  get totalBaskets(): number { return this.state?.baskets.length ?? 0; }

  clearLastScan(): void {
    this.lastScan = null;
  }

  finish(): void {
    if (!this.state) return;
    const missing = this.state.baskets.reduce((sum, b) =>
      sum + b.medications.filter(m => m.pickScanStatus === 'NotPicked').length, 0);
    if (missing > 0 && !confirm(`${missing} medications still not picked. Mark as missing and finish?`)) return;
    this.finishing = true;
    this.api.finishPick(this.batchId).subscribe({
      next: () => this.router.navigate(['/batchlist']),
      error: () => { this.finishing = false; },
    });
  }

  /** The basket's lines on the scan screen, to look at: this batch is scanned and finished here. */
  openBatchDetail(b?: BasketDetail) {
    this.router.navigate(['/batch', this.batchId], { queryParams: b ? { rx: b.rxId, mode: 'view' } : { mode: 'view' } });
  }

  basketComplete(b: BasketDetail): boolean {
    if (!b || b.medications.length === 0) return false;
    return b.medications.every(m =>
      m.pickScanStatus === 'Picked' ||
      m.pickScanStatus === 'PickedAsSubstitute' ||
      m.pickScanStatus === 'Missing');
  }

  medPicked(m: MedicationDetail): boolean {
    return m.pickScanStatus === 'Picked' || m.pickScanStatus === 'PickedAsSubstitute';
  }

  private triggerPulse(basket: number) {
    this.pulseBasket = null;
    queueMicrotask(() => {
      this.pulseBasket = basket;
      setTimeout(() => {
        if (this.pulseBasket === basket) this.pulseBasket = null;
      }, 900);
    });
  }

  trackByBasket(_: number, b: BasketDetail) { return b.basketNumber; }
  trackByMed(_: number, m: { medicationId: string }) { return m.medicationId; }
}
